/**
 * IntroVideo — 메인 진입 시마다 인트로 영상 재생 (요구사항 #255 + 후속 지시)
 * - 페이지 reload 마다 무조건 표시 (sessionStorage 제한 없음)
 * - SPA 라우팅 내부 전환에서는 한 번 닫으면 재표시 안 함 (state 유지)
 * - 자동 재생을 위해 muted + playsInline 필수 (iOS Safari 대응)
 * - 영상 종료(onEnded) / 배경 클릭 → 닫기(메인으로, fade-out)
 * - 영상 파일: /public/intro.mp4 (1080×1920, 6.7s, 11MB)
 */

import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Play } from "lucide-react";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";

// 같은 세션(탭)에서 이미 본 영상을 SW controllerchange reload / 사용자 reload 어느 경우에도
// 다시 표시하지 않는다 (= "메인 잠깐 보였다가 영상 또 나옴" 부작용 차단).
// 새 탭은 sessionStorage 비어있어 정상 표시.
const SS_SHOWN_KEY = "intro-shown";

export default function IntroVideo() {
  const location = useLocation();
  const videoRef = useRef<HTMLVideoElement>(null);
  // 이 탭이 메인("/") 이 아닌 경로로 처음 진입했다면 인트로 영구 skip.
  // 그러지 않으면 약관/정책 페이지에서 뒤로가기 → / 로 갔을 때 인트로가 첫 진입처럼 떠 버림.
  if (
    typeof window !== "undefined" &&
    !window.sessionStorage?.getItem(SS_SHOWN_KEY) &&
    window.location.pathname !== "/"
  ) {
    try { window.sessionStorage.setItem(SS_SHOWN_KEY, "1"); } catch { /* private mode */ }
  }
  const [visible, setVisible] = useState<boolean>(
    () => !(typeof window !== "undefined" && window.sessionStorage?.getItem(SS_SHOWN_KEY))
  );
  const [fading, setFading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(containerRef, visible);

  const safetyRef = useRef<number | null>(null);
  // 근본 원인: React 는 JSX 의 muted={true} 를 DOM "속성(attribute)"으로만 반영할 뿐,
  // 브라우저가 자동재생 허용 여부를 판단할 때 실제로 보는 "프로퍼티(video.muted)"와
  // 타이밍이 어긋날 수 있음(널리 알려진 React/video 이슈) — 크롬은 관대해서 안 드러나지만
  // 사파리는 엄격하게 프로퍼티를 확인해서 자동재생이 막히고 재생 버튼만 뜨는 원인이 됨.
  // 정석 해법: play() 호출 직전에 JS로 muted/defaultMuted 를 직접, 명시적으로 강제한다.
  const [needsTap, setNeedsTap] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.defaultMuted = true;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => setNeedsTap(true));
    }
    return () => {
      if (safetyRef.current !== null) {
        window.clearTimeout(safetyRef.current);
        safetyRef.current = null;
      }
    };
  }, []);

  // 안전 타이머만 설정 (영상 duration+1s — onEnded 누락 대비). 페이드인 제거 = 깜빡임 1회 차단
  const handlePlaying = () => {
    if (safetyRef.current !== null) return;
    const dur = videoRef.current?.duration || 7;
    safetyRef.current = window.setTimeout(() => close(), Math.ceil((dur + 1) * 1000));
  };

  const close = () => {
    if (safetyRef.current !== null) {
      window.clearTimeout(safetyRef.current);
      safetyRef.current = null;
    }
    // 같은 세션 재표시 차단 플래그
    try { window.sessionStorage.setItem(SS_SHOWN_KEY, "1"); } catch { /* private mode */ }
    // 비디오 메모리 즉시 해제 — src 비우고 load() 호출로 디코더 자원 회수 (11MB intro.mp4)
    try {
      const v = videoRef.current;
      if (v) {
        v.pause();
        v.removeAttribute("src");
        v.load();
      }
    } catch { /* defensive */ }
    setFading(true);
    window.setTimeout(() => {
      setVisible(false);
    }, 300);
  };

  // 메인 페이지("/") 에서만 인트로 표시. 약관/정책/로그인 등 별도 라우트는 우회
  if (location.pathname !== "/") return null;
  if (!visible) return null;

  return (
    <div
      ref={containerRef}
      // 요구사항 #541: 데스크탑 웹 접속 시 인트로 영상이 화면 전체를 덮는 게 과하다는 지적 —
      // 모바일은 기존처럼 전체화면 유지(앱 같은 느낌 의도된 부분), 데스크탑(md+)만 어두운
      // 배경 위에 작은 카드로 축소 표시.
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-white md:bg-black/70 transition-opacity duration-300 ${
        fading ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
      onClick={() => {
        // 재생 대기 상태에서는 배경 클릭도 "재생"으로 처리 (닫힘으로 오인되지 않도록)
        if (needsTap) {
          const v = videoRef.current;
          v?.play().then(() => setNeedsTap(false)).catch(() => {});
          return;
        }
        close();
      }}
      role="presentation"
    >
      {/* 요구사항 #319 — 모바일 풀스크린. 이전 w-screen/h-screen(100vw·100vh)은 모바일
          웹브라우저(주소창 토글)에서 100vh 가 실제 가시영역과 달라 상하 여백 발생.
          부모(fixed inset-0)를 그대로 채우는 absolute inset-0 + object-cover 로 교정 →
          PWA·웹 모두 실제 viewport 정확히 채움 (좌우는 cut 가능)
          요구사항 #541: 데스크탑(md+)만 static 으로 전환해 부모의 flex 중앙정렬을 따르는
          고정 크기(9:16 비율) 카드로 축소. */}
      <div
        className="absolute inset-0 md:static md:w-[380px] md:aspect-[9/16] md:h-auto md:max-h-[85vh] md:rounded-2xl md:overflow-hidden md:shadow-2xl"
      >
        <video
          ref={videoRef}
          src="/intro.mp4"
          poster="/intro-poster.jpg"
          muted
          playsInline
          preload="auto"
          onPlaying={handlePlaying}
          onEnded={() => close()}
          onError={() => close()}
          // #541: 카드 자체가 9:16 비율이라 레터박스 불필요 — 모바일/데스크탑 공통 object-cover
          className="absolute inset-0 h-full w-full object-cover pointer-events-none"
        />
        {/* 자동재생이 막혔을 때만 노출 — 어디를 눌러도(배경 포함) 재생되므로 순수 시각적 힌트 */}
        {needsTap && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
              <Play className="h-8 w-8 text-white" fill="white" />
            </div>
          </div>
        )}
        {/* 요구사항 #277-2 — X 버튼 제거 (영상 자동 종료 또는 배경 클릭으로 닫기) */}
      </div>
    </div>
  );
}
